import type {
  IpJsonContent,
  MarlinStore,
} from "@observe-tech/marlin-store/core";
import type { PreloadIpJsonFileMethods } from "@observe-tech/utils";

type SupportBridge = Partial<
  Pick<PreloadIpJsonFileMethods, "onSupportStoreRequest">
>;

export const handleSupportRequest = async (
  store: MarlinStore,
  request: unknown,
): Promise<Record<string, unknown[]>> => {
  if (typeof request !== "object" || request === null || !("action" in request))
    throw new Error("Invalid store support request");
  if (request.action === "importIpJson") {
    if (
      !("content" in request) ||
      typeof request.content !== "object" ||
      request.content === null ||
      !("SITES" in request.content) ||
      !Array.isArray(request.content.SITES)
    )
      throw new Error("No sites found in the selected file");
    const content = request.content as IpJsonContent;
    await store.settings.importIpJsonContent(content);
  } else if (request.action !== "snapshot") {
    if (request.action !== "saveSite" && request.action !== "removeSite")
      throw new Error("Invalid store support action");
    if (
      !("site" in request) ||
      typeof request.site !== "object" ||
      request.site === null ||
      !("_id" in request.site) ||
      typeof request.site._id !== "number"
    )
      throw new Error("Invalid machine");
    const previous = await store.sites.getMachine(request.site._id);
    if (!previous) throw new Error("Machine no longer exists");
    if (request.action === "removeSite") {
      await store.sites.removeSite(previous);
    } else {
      const site = request.site;
      if (
        !("name" in site) ||
        typeof site.name !== "string" ||
        !("ip" in site) ||
        typeof site.ip !== "string" ||
        !("port" in site) ||
        typeof site.port !== "string"
      )
        throw new Error("Invalid machine fields");
      await store.sites.saveSite({
        ...previous,
        name: site.name,
        ip: site.ip,
        port: site.port,
      });
    }
  }
  return store.adoption.snapshot();
};

export const subscribeToIpJsonEdits = (store: MarlinStore): (() => void) => {
  const files = store.capabilities.files;
  if (!files) return () => undefined;

  const stopEdits = files.onIpJsonEdited?.((content) =>
    store.settings.saveIpJsonContent(content),
  );
  const stopReads = files.onReadIpJson?.(() =>
    store.settings.getIpJsonContent(),
  );
  const bridge = (window as unknown as { electronAPI?: SupportBridge })
    .electronAPI;
  const stopSupport = bridge?.onSupportStoreRequest?.((request) =>
    handleSupportRequest(store, request),
  );
  return () => {
    stopEdits?.();
    stopReads?.();
    stopSupport?.();
  };
};
